/**
 * Alert relevance for the camera team (round 3, lane ALERTS & TEAM).
 *
 * Two pure stages on top of the rule evaluator (alerts.ts):
 *
 *  1. `coalesceAlertDrafts` (server, before persisting): one physical event → one alert. A pass that
 *     trips the same rule kind for several crew / cameras / sites within COALESCE_WINDOW_MS becomes a
 *     single alert listing them; a range-ring alert raised by an aircraft that just took off next to
 *     the crew is folded into the takeoff alert.
 *  2. `buildAlertFeed` (each viewer): who an alert is about (mission-wide / mine / producer / others)
 *     and how loud it is for THIS viewer — `important` (toast + optional chime: takeoff, landing,
 *     broadcasts, "coming toward you") or `info` (list only, no acknowledgement, expires from the
 *     default view after INFO_TTL_MS).
 *
 * Wording stays informational: nothing here adds or implies clear / safe / cleared / no traffic.
 */
import type { AlertDraft, Alert, AlertRule, AlertRuleSpec, Camera, EpochMs, Id, LatLon, MissionBundle, Role, TargetRef } from './types.ts';
import { distanceM } from './geo.ts';

export type AlertKind = AlertRuleSpec['type'] | 'broadcast' | 'other';

type AlertLike = Pick<Alert, 'ruleId' | 'dedupeKey' | 'title' | 'subjects'>;
type RuleLookup = ReadonlyMap<Id, Pick<AlertRule, 'spec'>> | readonly Pick<AlertRule, 'id' | 'spec'>[];

function lookup(rules: RuleLookup, id: Id): Pick<AlertRule, 'spec'> | undefined {
  if (rules instanceof Map) return rules.get(id);
  return (rules as readonly Pick<AlertRule, 'id' | 'spec'>[]).find((r) => r.id === id);
}

/** Rule type behind an alert (rule lookup first, then the stable dedupe-key shapes). */
export function alertKind(a: AlertLike, rules: RuleLookup): AlertKind {
  const r = a.ruleId ? lookup(rules, a.ruleId) : undefined;
  if (r) return r.spec.type;
  const k = a.dedupeKey;
  if (k.startsWith('broadcast:')) return 'broadcast';
  if (k.includes('terrain-mask:')) return 'terrain_mask';
  if (/:(takeoff|landing):\d+$/.test(k)) return 'phase_change';
  if (/:stale$|:back$/.test(k)) return 'stale_data';
  if (k.includes('|wx:')) return 'weather_change';
  if (k.includes('|sun:')) return 'sun';
  if (k.includes('|rs:')) return 'runsheet_due';
  return 'other';
}

// ───────────────────────────── 1. coalescing (server) ─────────────────────────────

/** Drafts of a groupable kind for the same lead subject inside this window become one alert. */
export const COALESCE_WINDOW_MS = 3000;
/** A range-ring alert this close to a takeoff of the same aircraft is part of the takeoff. */
export const TAKEOFF_FOLD_BEFORE_MS = 6000;
export const TAKEOFF_FOLD_AFTER_MS = 60_000;

const GROUPED_KINDS = new Set<AlertKind>(['proximity', 'eta', 'cpa', 'terrain_mask', 'weather_change']);

export interface AlertCoalescerState {
  pending: Record<string, { openedAt: EpochMs; drafts: AlertDraft[] }>;
  /** Last takeoff time per aircraft. */
  takeoffAt: Record<Id, EpochMs>;
}

export function emptyCoalescerState(): AlertCoalescerState {
  return { pending: {}, takeoffAt: {} };
}

const leadSubject = (subjects: readonly TargetRef[]): TargetRef | undefined =>
  subjects.find((s) => s.kind === 'aircraft') ?? subjects.find((s) => s.kind === 'airport') ?? subjects[0];

/** "HERON H01 — 0.1 NM FROM TOVE" → ["HERON H01", "0.1 NM FROM TOVE"]. */
export function splitAlertTitle(title: string): [string, string] {
  const i = title.indexOf(' — ');
  return i > 0 ? [title.slice(0, i), title.slice(i + 3)] : ['', title];
}

const sameRef = (a: TargetRef, b: TargetRef) => a.kind === b.kind && a.id === b.id;

/** Label a merged tail lists after "ALSO": the text after the last FROM/TO (or the whole tail). */
function tailTarget(tail: string): string {
  const m = /\b(?:FROM|TO|REACHES)\s+(.+?)(?:\s+IN\s+~.*)?$/.exec(tail);
  return (m?.[1] ?? tail).trim();
}

/**
 * Merge drafts of one event: the earliest keeps its headline, the others are listed —
 * "HERON H01 — PASSES 1,060 FT FROM JAKE IN ~30 S · ALSO CAM SITE"; the body lists every
 * target's own line. Subjects are the union; severity/tier the loudest.
 */
export function mergeAlertDrafts(list: readonly AlertDraft[]): AlertDraft {
  const sorted = [...list].sort((a, b) => a.t - b.t);
  const first = sorted[0]!;
  if (sorted.length === 1) return first;
  const [head, tail0] = splitAlertTitle(first.title);
  const tails = sorted.map((d) => splitAlertTitle(d.title)[1]);
  const others = [...new Set(tails.slice(1).map(tailTarget))].filter((x) => x && x !== tailTarget(tail0));
  const subjects: TargetRef[] = [];
  for (const d of sorted) for (const s of d.subjects) if (!subjects.some((x) => sameRef(x, s))) subjects.push(s);
  const rank = { info: 0, advisory: 1, warning: 2 } as const;
  const severity = sorted.reduce((s, d) => (rank[d.severity] > rank[s] ? d.severity : s), first.severity);
  const tier = sorted.reduce<number>((t, d) => Math.min(t, d.tier ?? 3), first.tier ?? 3) as AlertDraft['tier'];
  const title = others.length ? `${head ? `${head} — ` : ''}${tail0} · ALSO ${others.join(', ')}` : first.title;
  return {
    ...first,
    severity,
    tier,
    title: title.length > 200 ? `${title.slice(0, 197)}…` : title,
    body: tails.join(' · '),
    subjects,
  };
}

/**
 * Coalescing stage between the evaluator and persistence. Groupable kinds wait up to `windowMs`
 * for siblings (same kind + same lead subject); everything else passes straight through. Call on
 * every tick (with no new drafts too) so pending groups flush on time. Pure: returns a new state.
 */
export function coalesceAlertDrafts(
  incoming: readonly AlertDraft[],
  prev: AlertCoalescerState,
  now: EpochMs,
  opts: { rules: RuleLookup; windowMs?: number },
): { drafts: AlertDraft[]; state: AlertCoalescerState } {
  const windowMs = opts.windowMs ?? COALESCE_WINDOW_MS;
  const state: AlertCoalescerState = { pending: { ...prev.pending }, takeoffAt: { ...prev.takeoffAt } };
  const out: AlertDraft[] = [];
  for (const d of incoming) {
    const kind = alertKind(d, opts.rules);
    if (kind === 'phase_change' && /:takeoff:/.test(d.dedupeKey)) {
      const ac = d.subjects.find((s) => s.kind === 'aircraft');
      if (ac) state.takeoffAt[ac.id] = d.t;
    }
    if (!GROUPED_KINDS.has(kind) || windowMs <= 0) {
      out.push(d);
      continue;
    }
    const lead = leadSubject(d.subjects);
    const key = `${kind}|${lead ? `${lead.kind}:${lead.id}` : d.dedupeKey}`;
    const g = state.pending[key];
    state.pending[key] = g ? { openedAt: g.openedAt, drafts: [...g.drafts, d] } : { openedAt: d.t, drafts: [d] };
  }
  for (const [key, g] of Object.entries(state.pending)) {
    if (windowMs > 0 && now - g.openedAt < windowMs) continue;
    delete state.pending[key];
    const kind = key.split('|')[0] as AlertKind;
    if (kind === 'proximity') {
      const ac = g.drafts[0] ? leadSubject(g.drafts[0].subjects) : undefined;
      const tk = ac?.kind === 'aircraft' ? state.takeoffAt[ac.id] : undefined;
      // "0.1 NM FROM TOVE" at liftoff: the aircraft started next to the crew — the takeoff alert says it.
      if (tk != null && g.openedAt >= tk - TAKEOFF_FOLD_BEFORE_MS && g.openedAt <= tk + TAKEOFF_FOLD_AFTER_MS) continue;
    }
    out.push(mergeAlertDrafts(g.drafts));
  }
  out.sort((a, b) => a.t - b.t);
  return { drafts: out, state };
}

// ───────────────────────────── 2. per-viewer feed (each phone) ─────────────────────────────

/** A place within this distance of me or my camera is "my site". */
export const MY_SITE_RADIUS_M = 300;
/** Info alerts leave the default ("For me") list after this. */
export const INFO_TTL_MS = 10 * 60_000;
/** Run-sheet countdowns ("PASS 2 IN 1 MIN") leave the default list after this. */
export const RUNSHEET_INFO_TTL_MS = 2 * 60_000;
/** One "coming toward you" heads-up per aircraft in this window; later ones in it are info. */
export const APPROACH_QUIET_MS = 75_000;

export interface AlertViewer {
  userId: Id | null;
  producer: boolean;
  cameraIds: ReadonlySet<Id>;
  locationIds: ReadonlySet<Id>;
  /** Upper-case label alerts use for me ("JAKE"); null when not a member. */
  crewLabel: string | null;
  quietAircraftIds?: ReadonlySet<Id>;
}

type CameraExt = Camera & { locationId?: Id | null };

/**
 * Who "me" is for alerts: my crew target, the cameras I run, and the places at / near me or my
 * cameras (or a camera's assigned place). `myPos` = my latest fix; `cameraPositions` = resolved
 * camera positions (world state) for manned cameras that follow their operator.
 */
export function alertViewer(
  bundle: Pick<MissionBundle, 'cameras' | 'locations' | 'members'> & Partial<Pick<MissionBundle,'aircraft'>>,
  userId: Id | null,
  role: Role | null,
  opts: { myPos?: LatLon | null; cameraPositions?: ReadonlyMap<Id, LatLon | null>; siteRadiusM?: number } = {},
): AlertViewer {
  const radius = opts.siteRadiusM ?? MY_SITE_RADIUS_M;
  const mine = userId ? (bundle.cameras as CameraExt[]).filter((c) => c.operatorUserId === userId) : [];
  const cameraIds = new Set(mine.map((c) => c.id));
  const locationIds = new Set<Id>();
  const anchors: LatLon[] = [];
  if (opts.myPos) anchors.push(opts.myPos);
  for (const c of mine) {
    if (c.locationId) locationIds.add(c.locationId);
    const p = opts.cameraPositions?.get(c.id) ?? c.position;
    if (p) anchors.push(p);
  }
  for (const l of bundle.locations) {
    const r = Math.max(radius, l.radiusM ?? 0);
    if (anchors.some((p) => distanceM(p, { lat: l.lat, lon: l.lon }) <= r)) locationIds.add(l.id);
  }
  const m = userId ? bundle.members.find((x) => x.userId === userId) : undefined;
  return {
    userId,
    producer: role === 'owner' || role === 'producer',
    cameraIds,
    locationIds,
    quietAircraftIds:new Set(bundle.aircraft?.filter(a=>a.role!=='subject').map(a=>a.id)??[]),
    crewLabel: m ? (m.callsign || m.displayName).toUpperCase() : null,
  };
}

/** mission = everyone (takeoff, landing, data, weather, light, run sheet, broadcasts); mine = about me / my camera / my site. */
export type AlertScope = 'mission' | 'mine' | 'producer' | 'others';
export type AlertLevel = 'important' | 'info';

const MISSION_KINDS = new Set<AlertKind>(['phase_change', 'stale_data', 'weather_change', 'sun', 'runsheet_due', 'broadcast']);
const PRODUCER_KINDS = new Set<AlertKind>(['separation', 'traffic_near_subject']);
const IMPORTANT_KINDS = new Set<AlertKind>(['phase_change', 'broadcast']);
/** "Coming toward you": important when it is about me. Range rings (proximity) stay info. */
const IMPORTANT_IF_MINE = new Set<AlertKind>(['eta', 'cpa']);

export function isAboutViewer(subjects: readonly TargetRef[], v: AlertViewer): boolean {
  return subjects.some(
    (s) => (s.kind === 'crew' && s.id === v.userId) || (s.kind === 'camera' && v.cameraIds.has(s.id)) || (s.kind === 'location' && v.locationIds.has(s.id)),
  );
}

export function alertScope(a: Pick<Alert, 'subjects'>, kind: AlertKind, v: AlertViewer): AlertScope {
  if (MISSION_KINDS.has(kind)) return 'mission';
  if (isAboutViewer(a.subjects, v)) return 'mine';
  if (PRODUCER_KINDS.has(kind)) return v.producer ? 'producer' : 'others';
  // unknown kinds about nobody in particular (aircraft / airport only) read as mission-wide
  if (kind === 'other' && !a.subjects.some((s) => s.kind === 'crew' || s.kind === 'camera' || s.kind === 'location')) return 'mission';
  return 'others';
}

/** The person who sent a broadcast already knows it: for them it is a list line, never a toast, push or unread. */
export function isOwnBroadcast(a: Pick<Alert, 'subjects'>, kind: AlertKind, userId: Id | null): boolean {
  return kind === 'broadcast' && !!userId && a.subjects.some((s) => s.kind === 'crew' && s.id === userId);
}

export function alertLevel(kind: AlertKind, scope: AlertScope): AlertLevel {
  if (IMPORTANT_KINDS.has(kind)) return 'important';
  if (scope === 'mine' && IMPORTANT_IF_MINE.has(kind)) return 'important';
  return 'info';
}

export interface AlertFeedItem {
  alert: Alert;
  kind: AlertKind;
  scope: AlertScope;
  level: AlertLevel;
  /** Shown in the default "For me" list (mission-wide, mine, or producer info for producers). */
  forMe: boolean;
  /** Headline from this viewer's side ("… 1,060 FT FROM YOU …"). */
  title: string;
}

/**
 * The viewer's side of a headline: in a merged alert that lists me under ALSO, my own line leads;
 * my crew label reads YOU ("PASSES 880 FT FROM YOU IN ~30 S").
 */
export function personalTitle(a: Pick<Alert, 'title' | 'body'>, v: AlertViewer): string {
  let title = a.title;
  const me = v.crewLabel;
  if (!me) return title;
  const [head, tail] = splitAlertTitle(title);
  const esc = me.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const mentions = new RegExp(`\\b(FROM|TO|REACHES) ${esc}\\b`);
  const alsoIdx = tail.indexOf(' · ALSO ');
  if (alsoIdx >= 0 && !mentions.test(tail.slice(0, alsoIdx))) {
    const mine = a.body.split(' · ').find((seg) => mentions.test(seg));
    if (mine) title = `${head ? `${head} — ` : ''}${mine}`;
  }
  return title.replace(new RegExp(`\\b(FROM|TO|REACHES) ${esc}\\b`, 'g'), '$1 YOU');
}

/**
 * Every alert from this viewer's side, newest first. A second "coming toward you" alert for the same
 * aircraft within APPROACH_QUIET_MS of the last important one is downgraded to info (one heads-up per approach).
 */
/** Chase/Watch motion cues remain in history; only Subjects interrupt by default. Warnings and broadcasts retain priority. */
export function quietAircraftAlert(a:Pick<Alert,'subjects'|'severity'>,kind:AlertKind,v:AlertViewer):boolean {
  const ids=a.subjects.filter(s=>s.kind==='aircraft').map(s=>s.id);
  return a.severity!=='warning' && kind!=='broadcast' && ids.length>0 && ids.every(id=>v.quietAircraftIds?.has(id));
}
export function buildAlertFeed(
  alerts: readonly Alert[],
  rules: RuleLookup,
  v: AlertViewer,
  opts: { approachQuietMs?: number } = {},
): AlertFeedItem[] {
  const quiet = opts.approachQuietMs ?? APPROACH_QUIET_MS;
  const lastApproach = new Map<Id, EpochMs>();
  const items: AlertFeedItem[] = [];
  for (const a of [...alerts].sort((x, y) => x.t - y.t)) {
    const kind = alertKind(a, rules);
    const scope = alertScope(a, kind, v);
    let level = isOwnBroadcast(a, kind, v.userId) || quietAircraftAlert(a,kind,v) ? 'info' : alertLevel(kind, scope);
    if (level === 'important' && IMPORTANT_IF_MINE.has(kind)) {
      const ac = a.subjects.find((s) => s.kind === 'aircraft')?.id ?? '';
      const last = lastApproach.get(ac);
      if (last != null && a.t - last < quiet) level = 'info';
      else lastApproach.set(ac, a.t);
    }
    items.push({ alert: a, kind, scope, level, forMe: scope !== 'others', title: personalTitle(a, v) });
  }
  return items.reverse();
}

export type AlertFilter = 'mine' | 'all';

/** "For me": mission-wide + mine (+ producer info for producers); info drops off after INFO_TTL_MS. "All": everything. */
export function isInAlertView(item: AlertFeedItem, filter: AlertFilter, now: EpochMs, infoTtlMs = INFO_TTL_MS): boolean {
  if (filter === 'all') return true;
  if (!item.forMe) return false;
  if (item.level === 'important') return true;
  // a run-sheet countdown is stale once its moment has passed
  const ttl = item.kind === 'runsheet_due' ? Math.min(infoTtlMs, RUNSHEET_INFO_TTL_MS) : infoTtlMs;
  return now - item.alert.t <= ttl;
}

/** Important alerts for me newer than `seenAt` (and not acknowledged by me on another device). */
export function unreadImportant(items: readonly AlertFeedItem[], seenAt: EpochMs, userId: Id | null): number {
  let n = 0;
  for (const i of items) if (i.forMe && i.level === 'important' && i.alert.t > seenAt && !(userId && i.alert.ackedBy.includes(userId))) n++;
  return n;
}
